import type { CanvasDoc } from '../store/types';
import { cloudUrl, isUploaded } from './assetCloud';
import { isExpensiveParams } from './sign';

/** Matches `asset://sha256-<32 hex>` with an optional `?<params>` suffix.
 *  capture group 1 = the asset id, capture group 2 = params (without `?`) or undefined. */
export const ASSET_RE = /asset:\/\/(sha256-[a-f0-9]{32})(?:\?([^"'\s>)]+))?/g;

/** Every distinct asset id referenced anywhere in the serialized doc. */
export function collectAssetIds(doc: CanvasDoc): string[] {
  const ids = new Set<string>();
  const json = JSON.stringify(doc);
  for (const m of json.matchAll(ASSET_RE)) ids.add(m[1]);
  return [...ids];
}

/** Distinct expensive (ai=/bits=16) refs in `html` that need a server signature. */
export function expensiveRefs(html: string): { id: string; params: string }[] {
  const seen = new Set<string>();
  const out: { id: string; params: string }[] = [];
  for (const m of html.matchAll(ASSET_RE)) {
    const params = m[2];
    if (!params || !isExpensiveParams(params)) continue;
    const key = `${m[1]}?${params}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ id: m[1], params });
  }
  return out;
}

/**
 * Replace `asset://<id>` (optionally `asset://<id>?<params>`) with the best
 * available URL. Resolution order:
 *  - uploaded + expensive (ai=/bits=16): the signed CDN url from `signed(id,
 *    params)` if minted, else the optimistic local image while it loads.
 *  - uploaded + cheap: the (unsigned) CDN transform url.
 *  - not uploaded: local objectURL via `lookup(id)`; null leaves the ref intact
 *    for a later re-render.
 * Pure — no IO.
 */
export function rewriteAssetRefs(
  html: string,
  lookup: (id: string) => string | null,
  signed?: (id: string, params: string) => string | null,
): string {
  return html.replace(ASSET_RE, (whole, id: string, params: string | undefined) => {
    if (isUploaded(id)) {
      if (params && isExpensiveParams(params)) {
        return signed?.(id, params) ?? lookup(id) ?? whole;
      }
      return cloudUrl(id, params);
    }
    return lookup(id) ?? whole;
  });
}
